/** Dutch. Same keys as en.ts; the type check fails when one is missing. */
import type { MessageKey } from './en';
import { refusalsNl } from './refusals';
import type { Message } from './types';

const n = (p: Record<string, string | number>): number => Number(p['n'] ?? 0);

export const nl: Record<MessageKey, Message> = {
  'app.name': 'Terrella',

  // Niets maakt nog fruitsoorten, steenheuvels, de oude hout- en steenmachines of de planeetpomp;
  // hun codes zijn gereserveerd en houden een naam.
  'kind.stone': 'Steen',
  'kind.soil': 'Aarde',
  'kind.grass': 'Gras',
  'kind.water': 'Water',
  'kind.sapling': 'Stekje',
  'kind.fruit-sapling': 'Fruitstekje',
  'kind.tree': 'Boom',
  'kind.fruit-tree': 'Fruitboom',
  'kind.hill': 'Steenheuvel',
  'kind.machine-wood': 'Houtmachine',
  'kind.machine-stone': 'Steenmachine',
  'kind.ore': 'Erts',
  'kind.infuser-ruin': 'Planeetpomp, in puin',
  'kind.infuser': 'Planeetpomp',
  'kind.hub': 'Hub',
  'kind.basalt': 'Basalt',
  'kind.gold': 'Goud',
  'kind.core': 'Kern',
  'kind.broken': 'Gebroken {kind}',
  'group.tree': 'Bos',
  'group.water': 'Diep water',

  'machine.crystal': 'Kristalresonator',
  'machine.crystal-many': 'kristalresonatoren',
  'machine.crystal-what': 'Staat op erts en put kristal uit dat erts en uit het erts eromheen.',
  'machine.stone': 'Steenhouwer',
  'machine.stone-many': 'steenhouwers',
  'machine.remove': 'Weghalen',
  'machine.remove-aria': 'Haal de {name} weg',
  'machine.stone-what': 'Staat op steen en hakt blokken uit die steen en uit de steen eromheen.',
  'machine.charge': 'Kristallading',
  'machine.charge-many': 'kristalladingen',
  'machine.charge-what':
    'Vastgezet op steen of erts zoemt hij een paar seconden en breekt dan de rots eronder tot puin dat je kunt verzamelen.',
  'machine.heavy-charge': 'Zware kristallading',
  'machine.heavy-charge-many': 'zware kristalladingen',
  'machine.heavy-charge-what': 'Breekt ook basalt en goud, en alle rots eromheen.',
  'machine.infuser': 'Aanlegger',
  'machine.infuser-many': 'aanleggers',
  'machine.infuser-what':
    'Laad hem met blokken: hij legt ze op de tegels eromheen, de laagste eerst. Erts maakt ertsgrond, steen steengrond, aarde vult een kuil.',

  'resource.wood': 'Hout',
  'resource.stone': 'Steen',
  'resource.crystal': 'Kristal',
  'resource.soil-block': 'Blok aarde',
  'resource.soil-block-many': 'Blokken aarde',
  'resource.stone-block': 'Blok steen',
  'resource.stone-block-many': 'Blokken steen',
  'resource.ore-block': 'Blok erts',
  'resource.ore-block-many': 'Blokken erts',
  'resource.basalt-block': 'Blok basalt',
  'resource.basalt-block-many': 'Blokken basalt',
  'resource.gold-block': 'Blok goud',
  'resource.gold-block-many': 'Blokken goud',
  'resource.amount': '{n} {resource}',
  'resource.and': 'en',
  'resource.nothing': 'niets',

  'tile.depth': 'Diepte {n} · ',
  'tile.height': 'Hoogte {n} · ',
  'hint.ore': 'Een kristalvoorraad: zet er een kristalresonator op vanuit het bouwmenu.',
  'hint.stone': 'Kale steen: zet er een steenhouwer op, of breek hem met een kristallading.',
  'hint.soil': 'Losse aarde: verzamel hem met de hand.',
  'hint.basalt': 'Basalt: een zware kristallading breekt het.',
  'hint.gold': 'Goud, blootgelegd: een zware kristallading breekt het.',
  'hint.core': 'De kern: niets breekt hem.',
  'kind.rock': 'vaste rots',
  'hint.broken': 'Verzamel het met de hand.',
  'hint.fuse': 'Breekt de rots over {time}.',
  'hint.lay': '{block} landt over {time}.',
  'hint.load': 'Laad hem met blokken.',
  'hint.nowhere': 'Nergens om te leggen: elke tegel eromheen is bezet.',
  'hint.hub': 'Nodig iedereen uit met de QR-code, ga een wereld binnen of maak er een.',
  'hint.sapling': 'Wordt over {time} een boom.',
  'hint.tree': 'Drie bij elkaar vormen een bos.',
  'hint.water': 'Drie bij elkaar vormen diep water.',
  'hint.drawn': (p) =>
    n(p) === 1 ? `Een ${String(p['name'])} put eruit.` : `${n(p)} ${String(p['names'])} delen het.`,

  'action.collect': 'Oogst',
  'action.collect-aria': 'Oogst {n} {resource} uit de {name}',
  'action.collect-empty-aria': 'Oogsten: de {name} heeft nog niets',
  'action.empty': 'nog niets',
  'action.holds': '{n} {resource}',
  'action.holds-blocks': (p) => (n(p) === 1 ? '1 blok' : `${n(p)} blokken`),
  'action.fits': '{fit} van {n} passen',
  'action.full': 'zakken vol',
  'action.waiting': 'wachten',
  'action.info': 'Waarden',
  'action.info-aria': 'De waarden van de {name}',
  'action.hub': 'Hub',
  'action.hub-sub': 'uitnodigen · reizen',
  'action.hub-aria': 'Open de hub: nodig iedereen uit, ga een wereld binnen of maak er een',
  'action.gather': 'Verzamel',
  'action.gather-aria': 'Verzamel een {block} van deze tegel',
  'action.load': 'Laden',
  'action.load-aria': 'Laad je blokken in de {name}',
  'action.load-kinds': (p) => (n(p) === 1 ? '1 soort blok' : `${n(p)} soorten blokken`),
  'action.load-none': 'geen blokken',
  'action.hopper-full': 'trechter vol',
  'load.title': 'Laad de {name}',
  'load.pick-aria': 'Laad je {block} erin',
  'load.note':
    'Hij legt ze op de tegels eromheen in de volgorde waarin ze erin gaan, de laagste tegel eerst.',

  'hud.pockets-aria': 'Je hebt {wood} hout, {stone} steen en {crystal} kristal',
  'hud.admin': 'Beheerder',
  'hud.pockets-admin-aria': 'Beheerdersmodus: onbeperkte grondstoffen en zakken',
  'hud.canvas-aria':
    'De planeet van je wereld. Sleep of gebruik de pijltjestoetsen om te lopen. De knoppen onderaan tonen wat je kunt doen waar je staat; B opent het bouwmenu.',
  'hud.you': 'Jij',
  'hud.crewmate': 'Een speler',
  'hud.machine': 'machine',
  'hud.chip-alone-aria':
    'Wereld {world}: de code en instellingen. Niemand anders is nu op de planeet',
  'hud.chip-aria': (p) =>
    `Wereld ${String(p['world'])}: de code en instellingen. ${String(p['names'])} ${n(p) === 1 ? 'is' : 'zijn'} nu op de planeet`,

  // Het welkomstscherm
  'welcome.title': 'Welkom bij Terrella',
  'welcome.lede':
    'Bouw samen een kleine planeet op: zet machines op de grond en oogst wat ze eruit halen.',
  'welcome.login': 'Aanmelden',
  'welcome.register': 'Account maken',
  'welcome.signed-in-as': 'Aangemeld als {name}',
  'welcome.guest-as': 'Je speelt als {name}, een gast',
  'welcome.logout': 'Afmelden',
  'welcome.pick': 'Kies een wereld',
  'welcome.pick-text':
    'Ga een wereld van een vriend binnen met de code, of begin een nieuwe en deel jouw code.',
  'welcome.join': 'Een wereld binnengaan',
  'welcome.join-text': 'Vul de code in die je vriend je gaf.',
  'welcome.code-placeholder': 'Wereldcode, zoals ABCD EFGH',
  'welcome.code-aria': 'Wereldcode',
  'welcome.join-btn': 'Binnengaan',
  'welcome.create': 'Een wereld maken',
  'welcome.create-text': 'Geef hem een naam. Je krijgt een code om met je vrienden te delen.',
  'welcome.name-placeholder': 'Wereldnaam, zoals Nachtuilen',
  'welcome.name-aria': 'Wereldnaam',
  'welcome.create-btn': 'Maken',
  'welcome.back': 'Terug',
  'welcome.name-first': 'Geef je wereld eerst een naam.',
  'welcome.code-first': 'Vul eerst de code in.',

  // Het wereldpaneel
  'world.code': 'Wereldcode',
  'world.invite': 'Uitnodigen',
  'world.invite-hint': 'Wie de code scant of de link opent, staat meteen op deze planeet.',
  'world.qr-aria': 'De QR-code van de wereld: tik om hem groter te zien',
  'world.copy-link': 'Link kopiëren',
  'world.copied': 'Gekopieerd',
  'world.share': 'Delen',
  'world.share-text': 'Kom spelen op {world} in Terrella',
  'world.first-name': 'Wereld van {name}',

  'hub.title': 'De hub',
  'hub.invite-hint':
    'Wie de code hierboven scant, staat meteen op deze planeet: in de Terrella-app, of in de browser als gast.',
  'hub.guest-create': 'Je speelt als gast. Maak een account om een eigen wereld te maken.',
  'world.info': (p) => (Number(p['tiles']) === 1 ? '1 tegel' : `${Number(p['tiles'])} tegels`),
  'world.testing': 'Testen',
  'world.lab': 'Tegellab',
  'world.skip': '6 uur overslaan',
  'world.skip-done': '6 uur overgeslagen, voor iedereen in de wereld.',
  'world.give': 'Vul mijn zakken',
  'world.test-planet': 'Testplaneet',
  'world.test-planet-done': 'De planeet is opnieuw gemaakt, met een gat tot aan de kern.',
  'world.admin': 'Beheerdersmodus',
  'world.admin-on': 'Beheerdersmodus: alles is gratis en je zakken hebben geen grens.',
  'world.admin-off': 'Beheerdersmodus staat uit: je zakken houden wat ze hadden, tot hun ruimte.',
  'world.give-done': 'Je zakken zijn gevuld.',
  'world.testing-note':
    'Alleen ontwikkelversies tonen deze. Ze veranderen de planeet voor iedereen.',
  'world.leave': 'Wereld verlaten',
  'world.leave-confirm': '{world} verlaten? Alles wat je liet groeien blijft op de planeet.',
  'world.stay': 'Blijven',

  'build.title': 'Bouwen',
  'build.open': 'Bouwen',
  'build.open-aria': 'Open het bouwmenu',
  'build.pick-aria': 'Zet een {name} neer',
  'build.free': 'Gratis',
  'place.confirm': 'Zet neer',
  'place.confirm-aria': 'Zet de {name} hier neer',
  'place.cancel': 'Stop met neerzetten',

  // Refusals in words: a short reason for a button, a sentence per code (refusals.ts)
  ...refusalsNl,

  'info.level': 'Niveau {n}',
  'info.holds': 'Bevat',
  'info.intake': 'Neemt op',
  'info.reach': 'Put uit',
  'info.per': 'per {period}',
  'info.tiles': (p) =>
    n(p) === 1 ? `${String(p['ground'])}tegel` : `${String(p['ground'])}tegels`,
  'info.steps': (p) =>
    n(p) === 0 ? 'zijn eigen tegel' : n(p) === 1 ? 'bereik 1 stap' : `bereik ${n(p)} stappen`,
  'info.shared': 'Grond die hij deelt met een andere machine geeft elk zijn deel.',
  'info.shared-tiles': (p) => (n(p) === 1 ? '1 gedeeld' : `${n(p)} gedeeld`),
  'info.full': 'Vol: hij neemt niets meer op tot je oogst.',
  'info.fuse': 'Lont',
  'info.fuse-left': 'Breekt over {time}',
  'info.breaks': 'Breekt',
  'info.area-own': 'de rots eronder',
  'info.area-ring': 'de rots eronder en alles eromheen',
  'info.area': 'Bereik',
  'info.hopper': 'Trechter',
  'info.blocks': 'blokken',
  'info.laying': 'Leggen',
  'info.lay-what': '{block}, op de verlichte tegel',
  'info.idle-empty': 'Leeg: laad hem met blokken.',
  'info.idle-nowhere': 'Nergens om te leggen: elke tegel eromheen is vol of bezet.',
  'info.ring': 'Legt op',
  'info.ring-takes': (p) =>
    n(p) === 1 ? '1 tegel neemt een blok' : `${n(p)} tegels nemen een blok`,
  'info.tiles-broken': (p) => (n(p) === 1 ? '1 tegel rots' : `${n(p)} tegels rots`),

  'upgrade.title': 'Upgrades',
  'upgrade.open-aria': 'De upgrades van de {name}',
  'upgrade.available': 'Niveau {n} beschikbaar',
  'upgrade.next-soon': 'Niveau {n} komt binnenkort',
  'upgrade.top': 'Op zijn hoogste niveau',
  'upgrade.past': 'Gehaald',
  'upgrade.current': 'Huidig',
  'upgrade.next': 'Volgende',
  'upgrade.later': 'Later',
  'upgrade.after': 'Na niveau {n}',
  'upgrade.soon': 'Binnenkort',
  'upgrade.soon-text': 'Aan zijn volgende niveau wordt gewerkt.',
  'upgrade.takes': 'Duurt {time}',
  'upgrade.button': 'Upgrade',
  'upgrade.button-aria': 'Upgrade de {name} naar niveau {n}',
  'upgrade.underway': 'Wordt niveau {n}',
  'upgrade.left': 'nog {time}',
  'upgrade.working': 'Tot die tijd werkt hij door op zijn niveau.',

  // Wie er op de planeet is
  'online.title': 'Nu op de planeet',
  'online.others': 'Ook in deze wereld',
  'online.nobody': 'Er is nu verder niemand.',
  'online.you': 'Jij',
  'online.changes': (p) => (n(p) === 1 ? '1 verandering' : `${n(p)} veranderingen`),
  'online.here': 'nu hier',

  'away.title': 'Terwijl je weg was',
  'away.changes': (p) =>
    `${String(p['name'])} heeft ${n(p) === 1 ? '1 verandering' : `${n(p)} veranderingen`} aan de planeet gemaakt.`,
  'away.saplings': (p) =>
    n(p) === 1 ? '1 stekje is een boom geworden.' : `${n(p)} stekjes zijn bomen geworden.`,
  'away.fell': (p) => `Verhoogde grond is ${n(p) === 1 ? 'één keer' : `${n(p)} keer`} teruggezakt.`,
  'away.sank': (p) =>
    n(p) === 1
      ? 'Een vijver is gezakt naar een lagere oever.'
      : `${n(p)} vijvers zijn gezakt naar een lagere oever.`,
  'away.laid': (p) =>
    n(p) === 1 ? 'Een aanlegger legde een blok.' : `Aanleggers legden ${n(p)} blokken.`,
  'away.shattered': (p) =>
    n(p) === 1 ? 'Een lading brak een tegel rots.' : `Ladingen braken ${n(p)} tegels rots.`,

  'toast.fell': 'Verhoogde grond verloor zijn steun en zakte terug tot steen.',
  'toast.sank': (p) =>
    `Het water is gezakt naar niveau ${n(p) < 0 ? `−${-n(p)}` : String(n(p))}: het staat nooit hoger dan zijn hoogste oever.`,
  'toast.skipped': '{name} heeft 6 uur overgeslagen.',
  'toast.collected': 'Je hebt {n} {resource} geoogst.',
  'toast.upgrading': '{name} upgradet de {machine} naar niveau {n}.',
  'toast.upgraded': 'De {machine} is nu niveau {n}.',
  'toast.removed': '{name} heeft een {machine} weggehaald.',
  'toast.joined': '{name} is op de planeet.',
  'toast.member-joined': '{name} doet mee met de wereld.',
  'toast.member-left': '{name} heeft de wereld verlaten.',
  'toast.offline': 'Geen verbinding. Je veranderingen wachten tot hij terug is.',
  'toast.outdated': 'Terrella heeft een update nodig om verder te spelen.',
  'toast.rules-differ':
    'De spelregels zijn veranderd. Werk Terrella bij om je veranderingen meteen te zien.',

  'ui.close': 'Sluiten',
  'ui.back': 'Terug',
  'time.s': '{n} s',
  'time.min': '{n} min',
  'time.h': '{h} u',
  'time.hm': '{h} u {m} min',
};
